import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import { Exception } from '../../../../../../../../../api/homeSiteExceptions/types';
import { formatDateTimeMonth, useBrowserClosureWarning } from '../../../../../../../../../utils/functions';
import { Note } from '../../../../../../../../../api/homeSiteNotes/types';
import { ExpandMore } from '@mui/icons-material';
import { decisionDialogBoxTitle, decisionDialogBoxMessage } from '../../../../../../../../../components/decisionDialogBox/decisionDialogBox';
import { useDialogBoxContext } from '../../../../../../../../../components/decisionDialogBox/decisionDialogBoxContext';

interface UpdateExceptionFormProps {
    exception: Exception | null;
    notes: Record<string, Note[]>;
    title: string;
    handleSaveUpdate: (updateComment: string) => void;
    handleBack: () => void;
    onExceptionDirtyChange?: (dirty: boolean) => void;
    onSetExceptionUpdateHandler?: (fn: () => Promise<void>) => void;
}

const UpdateExceptionForm: React.FC<UpdateExceptionFormProps> = ({
    exception,
    notes,
    title,
    handleSaveUpdate,
    handleBack,
    onExceptionDirtyChange,
    onSetExceptionUpdateHandler,
}) => {
    const [updateComment, setUpdateComment] = useState('');
    const isSaveDisabled = !updateComment.trim() || updateComment.length > 200;
    const { showDecisionDialogBox } = useDialogBoxContext();
    const isTryingToLeave = updateComment.trim().length > 0;

    useEffect(() => {
      const isDirty = updateComment.trim().length > 0;
      onExceptionDirtyChange?.(isDirty);
    }, [updateComment, onExceptionDirtyChange]);

    const handleSave = useCallback(() => {
      handleSaveUpdate(updateComment);
      setUpdateComment("");
      onExceptionDirtyChange?.(false);
    }, [handleSaveUpdate, updateComment, onExceptionDirtyChange]);

    const handleCancel = () => {
      handleBack();
      setUpdateComment("");
      onExceptionDirtyChange?.(false);
    };

    useEffect(() => {
      onExceptionDirtyChange?.(updateComment.trim().length > 0);
    }, [onExceptionDirtyChange, updateComment]);

    useEffect(() => {
      if (onSetExceptionUpdateHandler) {
        onSetExceptionUpdateHandler(async () => {
          handleSave();
        });
      }
    }, [handleSave, onSetExceptionUpdateHandler]);

    useBrowserClosureWarning(isTryingToLeave);
    const handleBackButtonCheck = () => {
        if (isTryingToLeave) {
          showDecisionDialogBox(
            true,
            decisionDialogBoxTitle,
            decisionDialogBoxMessage,
            () => {
              handleSave()
              handleBack();
            },
            () => {
              handleBack();
            }
          );
        } else {
          handleBack();
        }
      };

    return (
        <Box>
            {/* Header */}
            <Box display="flex" alignItems="center" marginBottom={1} sx={{overflow: "hidden", position:'sticky', top:'0px',zIndex: 1000, backgroundColor: "white"}}>
                <IconButton onClick={handleBackButtonCheck}>
                    <ArrowBackIcon sx={{ color: "#1C2B36" }} />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "16px" }}>
                    {title}
                </Typography>
            </Box>

            {/* Exception Card */}
            <Card
                variant="outlined"
                style={{ marginBottom: '8px', backgroundColor: '#FFEFF1' }}
            >
                <CardContent>
                    <Typography
                        variant="h6"
                        sx={{
                            backgroundColor: "#C93939",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "12px",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            display: "inline-block",
                            marginBottom: "8px",
                        }}
                    >
                        {exception?.mileStoneName}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '14px' }}>
                        {exception?.description}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" marginTop="8px">
                        <Box display="flex" alignItems="center">
                            <AccountCircleOutlined />
                            <Typography
                                variant="caption"
                                marginLeft={1}
                            >
                                {exception?.createdBy}
                            </Typography>
                        </Box>
                        <Typography variant="caption">
                            {exception ? formatDateTimeMonth(exception.createdOnDate) : ''}
                        </Typography>
                    </Box>
                </CardContent>
                {/* Additional Updates Accordion */}
                {exception && notes[exception.id]?.length > 0 && (
                    <Accordion>
                        <AccordionSummary sx={{ backgroundColor: "#FFEFF1" }}
                            expandIcon={<ExpandMore />}
                            aria-controls={`panel-${exception.id}-content`}
                            id={`panel-${exception.id}-header`}
                        >
                            <Typography variant="subtitle1" sx={{ color: "#1C2B36", fontWeight: "700", fontSize: "12px" }}>
                                Additional Updates
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor: "#FFEFF1", }}>
                            {notes[exception.id].map((note, index) => (
                                <Box key={note.id || index}>
                                    <Typography variant="body2">
                                        {note.description}
                                    </Typography>
                                    <Box display="flex" justifyContent="space-between" marginTop="4px">
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <AccountCircleOutlined />
                                            <Typography variant="caption" marginLeft={1}>
                                                {note.createdBy}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption">
                                            {formatDateTimeMonth(note.createdOnDate)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}
            </Card>

            {/* Update Comment Input */}
            <InputLabel variant="standard" id="type-select-label" sx={{
           fontWeight: "500", 
           color: "#3E5463", 
           fontSize: "16px", 
           "& .MuiFormLabel-asterisk": {
           color: "red",
          },
        }} required>{title === "Update Exception" ? "Add your update comment" : "Add your resolve comment"}</InputLabel>
            <TextField
                multiline
                rows={4}
                value={updateComment}
                onChange={(event) => setUpdateComment(event.target.value)}
                fullWidth
                variant="standard"
                placeholder={title === "Update Exception" ? "Type update comment" : "Type resolve comment"}
                inputProps={{ maxLength: 200 }}
                sx={{
                  marginBottom: "8px",
                    "& .MuiFilledInput-root": {
                        fontFamily: "Karla, sans-serif"
                    },
                    "& .MuiInputLabel-filled": {
                        color: "#5B7282",
                        fontSize: "14px"
                    },
                }}
            />
            <Typography variant="caption">
              {updateComment.length}/280 characters
            </Typography>

            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={handleCancel} sx={{ textTransform: 'none', border: '2px solid #005DAA' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={isSaveDisabled}
                    onClick={handleSave}
                    sx={{ textTransform: 'none' }}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
};

export default UpdateExceptionForm;